import { useState, useRef, useEffect, useCallback } from 'react';
import {
  extractNewWords,
  wrapWordsWithAnimation,
  buildDisplayText,
} from './streaming-text-preprocessor';

const PAUSE_THRESHOLD_MS = 500;

interface UseStreamingTextProps {
  accumulatedText: string;
  isStreamActive: boolean;
}

interface UseStreamingTextResult {
  displayText: string;
  isStreaming: boolean;
  hasDisplayableText: boolean;
}

type StreamState = 'idle' | 'streaming' | 'paused' | 'complete';

export function useStreamingText({
  accumulatedText,
  isStreamActive,
}: UseStreamingTextProps): UseStreamingTextResult {
  const [streamState, setStreamState] = useState<StreamState>('idle');

  const processedIndexRef = useRef(0);
  const animatedHtmlRef = useRef('');
  const prevStreamActiveRef = useRef(false);
  // Tracks the current stream state in a ref so timer callbacks see the latest value
  const streamStateRef = useRef<StreamState>('idle');

  const updateStreamState = useCallback((next: StreamState) => {
    streamStateRef.current = next;
    setStreamState(next);
  }, []);

  // Detect stream start/end transitions
  useEffect(() => {
    const wasActive = prevStreamActiveRef.current;
    prevStreamActiveRef.current = isStreamActive;

    if (isStreamActive && !wasActive) {
      // Stream just started - reset and go straight to streaming
      processedIndexRef.current = 0;
      animatedHtmlRef.current = '';
      updateStreamState('streaming');
    }

    if (!isStreamActive && wasActive) {
      updateStreamState('complete');
    }
  }, [isStreamActive, updateStreamState]);

  // Handle text updates during streaming
  useEffect(() => {
    if (!isStreamActive || accumulatedText.length === 0) return;

    if (streamStateRef.current === 'paused') {
      updateStreamState('streaming');
    }

    const timer = setTimeout(() => {
      updateStreamState('paused');
    }, PAUSE_THRESHOLD_MS);

    return () => clearTimeout(timer);
  }, [accumulatedText, isStreamActive, updateStreamState]);

  // Compute display text
  const isStreaming = streamState !== 'idle' && streamState !== 'complete';

  if (streamState === 'idle') {
    return {
      displayText: accumulatedText,
      isStreaming: false,
      hasDisplayableText: accumulatedText.length > 0,
    };
  }

  if (streamState === 'complete') {
    return {
      displayText: accumulatedText,
      isStreaming: false,
      hasDisplayableText: true,
    };
  }

  // streaming or paused - process only NEW text since last render
  const newText = accumulatedText.slice(processedIndexRef.current);
  const { completeWords, bufferedPartial } = extractNewWords(
    newText,
    !isStreamActive
  );

  if (completeWords.length > 0) {
    animatedHtmlRef.current += wrapWordsWithAnimation(completeWords);
    processedIndexRef.current = accumulatedText.length - bufferedPartial.length;
  }

  // Don't show anything until first words are processed
  if (animatedHtmlRef.current === '') {
    return { displayText: '', isStreaming: true, hasDisplayableText: false };
  }

  const displayText = buildDisplayText(animatedHtmlRef.current);

  return { displayText, isStreaming, hasDisplayableText: true };
}
